import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { useBoundaryOverlap } from "../../stores/boundaryOverlap";
import { previewBox, ringsOf, overlapSummary } from "./boundaryPreview";
import type { BoundaryOverlapHit } from "../../types/contract";
import { Modal } from "../../ui/Modal";

// " Mounted globally; renders only while `useBoundaryOverlap` has a pending ask, so all 3 add-field
// workflows share one gate. The two shapes are drawn as an inline SVG in a SHARED projection rather
// than a map: this is a shape-vs-shape judgement, and spinning up a MapLibre instance inside a
// modal is heavy for that.
export function BoundaryOverlapConfirm() {
  const pending = useBoundaryOverlap((s) => s.queue[0] ?? null);
  const answer = useBoundaryOverlap((s) => s.answer);
  const removalFailed = useBoundaryOverlap((s) => s.removalFailed);
  const clearRemovalFailed = useBoundaryOverlap((s) => s.clearRemovalFailed);

  const box = useMemo(() => {
    if (!pending) return null;
    return previewBox(
      [
        pending.candidate,
        ...pending.hits.map((h: BoundaryOverlapHit) => h.boundary),
      ].filter(Boolean),
    );
  }, [pending]);

  // The user declined, but removing the field FAILED — say so plainly rather than leaving a silent
  // duplicate.
  if (!pending && removalFailed) {
    return (
      <Modal>
        <div className="fixed inset-0 z-confirm flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg border border-line bg-surface p-3 shadow-2xl">
            <div className="flex items-start gap-2">
              <AlertTriangle
                size={16}
                className="mt-0.5 shrink-0 text-red-400"
              />
              <p className="text-[12px] text-fg">
                “{removalFailed}” couldn’t be removed automatically — it’s still
                in your fields. You can delete it from the table.
              </p>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                className="rounded bg-accent px-2 py-1 text-xs font-medium text-canvas hover:bg-accent-hover"
                onClick={clearRemovalFailed}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  if (!pending) return null;
  const { name, hits, candidate, alreadyCreated } = pending;
  const many = hits.length > 1;

  return (
    <Modal>
      <div className="fixed inset-0 z-confirm flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-lg rounded-lg border border-line bg-surface shadow-2xl">
          <div className="flex items-start gap-2 border-b border-line px-3 py-2">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-amber-500"
            />
            <div>
              <h2 className="text-sm font-semibold">
                This boundary overlaps{" "}
                {many
                  ? `${hits.length} fields you already have`
                  : "a field you already have"}
              </h2>
              <p className="text-[11px] text-muted">
                “{name}” covers the same ground as{" "}
                {many
                  ? "these fields"
                  : `“${hits[0]?.field_name ?? "an existing field"}”`}
                . It may be the same field added twice.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 p-3 sm:flex-row">
            {box && (
              <svg
                width={box.width}
                height={box.height}
                viewBox={`0 0 ${box.width} ${box.height}`}
                className="shrink-0 self-center rounded border border-line bg-canvas"
                role="img"
                aria-label="The new boundary drawn over the existing field boundaries"
              >
                {/* existing fields underneath */}
                {hits.map((h, i) =>
                  ringsOf(h.boundary).map((r, j) => (
                    <path
                      key={`e${i}-${j}`}
                      d={box.path(r)}
                      fill="rgba(96,165,250,0.28)"
                      stroke="#60a5fa"
                      strokeWidth={1.25}
                    />
                  )),
                )}
                {/* the candidate on top */}
                {ringsOf(candidate).map((r, j) => (
                  <path
                    key={`c${j}`}
                    d={box.path(r)}
                    fill="rgba(245,158,11,0.30)"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                  />
                ))}
              </svg>
            )}

            <div className="min-w-0 flex-1">
              {candidate != null && (
                <div className="mb-1 flex items-center gap-1.5 text-[11px]">
                  <span
                    className="inline-block h-2 w-3 rounded-sm"
                    style={{
                      background: "rgba(245,158,11,0.5)",
                      border: "1px solid #f59e0b",
                    }}
                  />
                  <span className="text-fg">The new boundary</span>
                </div>
              )}
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px]">
                <span
                  className="inline-block h-2 w-3 rounded-sm"
                  style={{
                    background: "rgba(96,165,250,0.5)",
                    border: "1px solid #60a5fa",
                  }}
                />
                <span className="text-fg">Already in your account</span>
              </div>
              <ul className="max-h-40 space-y-1 overflow-auto">
                {hits.map((h) => (
                  <li
                    key={h.field_id}
                    className="rounded border border-line px-2 py-1 text-[11px]"
                  >
                    <div className="truncate font-medium text-fg">
                      {h.field_name}
                    </div>
                    <div className="text-faint">{overlapSummary(h)}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
            <button
              className="rounded px-2 py-1 text-xs text-muted hover:bg-hover"
              onClick={() => answer(false)}
            >
              {alreadyCreated ? "Don’t add it — remove" : "Cancel"}
            </button>
            <button
              className="rounded bg-accent px-2 py-1 text-xs font-medium text-canvas hover:bg-accent-hover"
              onClick={() => answer(true)}
            >
              Bring it in anyway
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
